import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import EntityFormDialog from "@/components/EntityFormDialog";
import { toast } from "sonner";
import {
  Building2,
  MapPin,
  Star,
  BedDouble,
  Plus,
  Search,
  FileSpreadsheet,
} from "lucide-react";
import { MiniSparkline } from "@/components/Sparkline";
import ImportExcelModal from "@/components/ImportExcelModal";

const HOTEL_THUMB = "https://images.unsplash.com/photo-1554009975-d74653b879f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbCUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MDMxODQ1NXww&ixlib=rb-4.1.0&q=85";

function scoreTone(score) {
  if (score == null) return { label: "N/D", color: "bg-muted text-muted-foreground", dot: "bg-muted-foreground" };
  if (score >= 85) return { label: "Eccellente", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", dot: "bg-emerald-500" };
  if (score >= 70) return { label: "Buono", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", dot: "bg-amber-500" };
  return { label: "Critico", color: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300", dot: "bg-rose-500" };
}

function Stars({ n }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
      ))}
    </div>
  );
}

function HotelCard({ hotel, onClick }) {
  const tone = scoreTone(hotel.punteggio_qualita);
  return (
    <div
      data-testid={`hotel-card-${hotel.id}`}
      onClick={onClick}
      className="ih-glass rounded-2xl overflow-hidden ih-card-hover cursor-pointer group"
    >
      <div className="relative h-40 overflow-hidden">
        <img
          src={HOTEL_THUMB}
          alt={hotel.nome}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />
        <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
          <div>
            <div className="text-white/80 text-[10px] uppercase tracking-[0.18em] mb-0.5 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {hotel.citta}
            </div>
            <h3 className="text-white font-display text-lg font-semibold leading-tight drop-shadow">{hotel.nome}</h3>
          </div>
          <Stars n={hotel.categoria_stelle} />
        </div>
      </div>
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5" /> {hotel.numero_camere} camere</div>
          <div className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${tone.color}`}>
            <span className="inline-flex items-center gap-1.5">
              <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} /> {tone.label}
            </span>
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-0.5">Punteggio qualità</div>
            <div className="font-display text-2xl tracking-tight font-medium">
              {hotel.punteggio_qualita != null ? `${Math.round(hotel.punteggio_qualita)}` : "—"}
              <span className="text-sm text-muted-foreground font-normal">/100</span>
            </div>
          </div>
          <div className="text-right text-[11px] text-muted-foreground">
            <div>Ultima ispezione</div>
            <div className="font-medium text-foreground">{hotel.ultima_ispezione ? new Date(hotel.ultima_ispezione).toLocaleDateString("it-IT") : "—"}</div>
          </div>
        </div>
        <div className="pt-2 border-t border-border/40">
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Trend 30gg</div>
          <MiniSparkline hotelId={hotel.id} days={30} height={36} />
        </div>
      </div>
    </div>
  );
}

const EMPTY = {
  nome: "", indirizzo: "", citta: "", numero_camere: 50, categoria_stelle: 4,
};

export default function Strutture() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [citta, setCitta] = useState("all");
  const [stelle, setStelle] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importRoomsBulkOpen, setImportRoomsBulkOpen] = useState(false);
  const [importHostfixOpen, setImportHostfixOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hotels");
      setHotels(data || []);
    } catch {
      toast.error("Impossibile caricare le strutture");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const citta_options = useMemo(() => Array.from(new Set(hotels.map((h) => h.citta))).sort(), [hotels]);
  const filtered = useMemo(() => {
    return hotels.filter((h) => {
      if (citta !== "all" && h.citta !== citta) return false;
      if (stelle !== "all" && String(h.categoria_stelle) !== stelle) return false;
      if (q && !`${h.nome} ${h.citta}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [hotels, q, citta, stelle]);

  const canCreate = user?.role === "super_admin";

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/hotels", { ...form, numero_camere: Number(form.numero_camere), categoria_stelle: Number(form.categoria_stelle) });
      toast.success("Struttura creata");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="strutture-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-2">
            <Building2 className="h-3 w-3" /> Strutture
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">
            Portfolio
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {loading ? "Caricamento…" : `${filtered.length} ${filtered.length === 1 ? "struttura" : "strutture"} visibili`}
          </p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <Button
              data-testid="import-hotels-btn"
              variant="outline"
              onClick={() => setImportOpen(true)}
              className="rounded-xl"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Importa da Excel
            </Button>
            <Button
              data-testid="import-rooms-bulk-btn"
              variant="outline"
              onClick={() => setImportRoomsBulkOpen(true)}
              className="rounded-xl"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Importa camere (multi-hotel)
            </Button>
            <Button
              data-testid="import-hostfix-btn"
              variant="outline"
              onClick={() => setImportHostfixOpen(true)}
              className="rounded-xl"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Importa da HostFix
            </Button>
            <Button
              data-testid="new-hotel-btn"
              onClick={() => { setForm(EMPTY); setOpen(true); }}
              className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Nuova struttura
            </Button>
          </div>
        )}
      </div>
      {importOpen && <ImportExcelModal entity="hotels" onClose={() => setImportOpen(false)} onComplete={load} />}
      {importRoomsBulkOpen && <ImportExcelModal entity="rooms-bulk" onClose={() => setImportRoomsBulkOpen(false)} onComplete={() => setImportRoomsBulkOpen(false)} />}
      {importHostfixOpen && <ImportExcelModal entity="hostfix-csv" onClose={() => setImportHostfixOpen(false)} onComplete={() => { setImportHostfixOpen(false); load(); }} />}

      {/* Filters */}
      <div className="ih-glass rounded-2xl p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="strutture-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome o città…"
            className="pl-9 h-10 bg-background/60"
          />
        </div>
        <Select value={citta} onValueChange={setCitta}>
          <SelectTrigger className="w-full sm:w-[200px] h-10" data-testid="filter-citta">
            <SelectValue placeholder="Città" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le città</SelectItem>
            {citta_options.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stelle} onValueChange={setStelle}>
          <SelectTrigger className="w-full sm:w-[160px] h-10" data-testid="filter-stelle">
            <SelectValue placeholder="Stelle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le stelle</SelectItem>
            <SelectItem value="5">5 stelle</SelectItem>
            <SelectItem value="4">4 stelle</SelectItem>
            <SelectItem value="3">3 stelle</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="ih-glass rounded-2xl h-72 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="ih-glass rounded-2xl p-12 text-center">
          <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <p className="font-medium">Nessuna struttura trovata</p>
          <p className="text-sm text-muted-foreground">Modifica i filtri o aggiungi una nuova struttura.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((h) => (
            <HotelCard key={h.id} hotel={h} onClick={() => navigate(`/strutture/${h.id}`)} />
          ))}
        </div>
      )}

      {/* Create modal */}
      <EntityFormDialog
        open={open}
        onOpenChange={setOpen}
        title="Nuova struttura"
        description="Aggiungi un nuovo hotel al portfolio."
        size="md"
        onSubmit={onCreate}
        submitLabel="Crea struttura"
        saving={saving}
        testId="hotel-form"
        submitTestId="hotel-form-submit"
      >
        <div className="space-y-2">
          <Label>Nome</Label>
          <Input data-testid="hotel-form-nome" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Indirizzo</Label>
          <Input required value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Città</Label>
            <Input required value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Stelle</Label>
            <Select value={String(form.categoria_stelle)} onValueChange={(v) => setForm({ ...form, categoria_stelle: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 stelle</SelectItem>
                <SelectItem value="4">4 stelle</SelectItem>
                <SelectItem value="5">5 stelle</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Numero camere</Label>
          <Input type="number" min={1} required value={form.numero_camere} onChange={(e) => setForm({ ...form, numero_camere: e.target.value })} />
        </div>
      </EntityFormDialog>
    </div>
  );
}
